/*
Copyright (C) 2023-2026 QuantumNous
（预览组件）
*/
import { useEffect, useMemo } from 'react'
import { Loader2, X, ExternalLink, FileText, FileSpreadsheet, FileType, FileImage } from 'lucide-react'

// ============================================================================
// 支持预览的文件类型
// ============================================================================

const PPT_EXTENSIONS = ['pptx', 'ppt', 'pot', 'potx', 'pps', 'ppsx', 'dps', 'dpt', 'pptm', 'potm', 'ppsm']
const DOC_EXTENSIONS = ['doc', 'dot', 'wps', 'wpt', 'docx', 'dotx', 'docm', 'dotm']
const XLS_EXTENSIONS = ['xls', 'xlt', 'et', 'ett', 'xlsx', 'xltx', 'csv', 'xlsb', 'xlsm', 'xltm', 'ets']
const PDF_EXTENSIONS = ['pdf']

const ALL_PREVIEW = [...PPT_EXTENSIONS, ...DOC_EXTENSIONS, ...XLS_EXTENSIONS, ...PDF_EXTENSIONS]

/** 判断文件是否可预览 */
export function isPreviewable(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return ALL_PREVIEW.includes(ext)
}

/** 获取文件扩展名 */
function getExt(fileName: string): string {
  return (fileName.split('.').pop() || '').toLowerCase()
}

/** 获取文件类型图标 */
function getFileIcon(fileName: string) {
  const ext = getExt(fileName)
  if (PPT_EXTENSIONS.includes(ext)) return FileType
  if (XLS_EXTENSIONS.includes(ext)) return FileSpreadsheet
  if (PDF_EXTENSIONS.includes(ext)) return FileImage
  return FileText
}

// ============================================================================
// 预览弹窗
// ============================================================================

type PreviewDialogProps = {
  open: boolean
  fileName: string
  previewUrl: string   // Office 文件的 COS 预签名 URL（Office Online 需要公网可访问）
  streamUrl: string     // PDF/下载的服务端代理流 URL（鉴权，不暴露 COS）
  loading: boolean
  onClose: () => void
}

export function PreviewDialog({ open, fileName, previewUrl, streamUrl, loading, onClose }: PreviewDialogProps) {
  const ext = getExt(fileName)
  const isPdf = PDF_EXTENSIONS.includes(ext)
  const isOffice = PPT_EXTENSIONS.includes(ext) || DOC_EXTENSIONS.includes(ext) || XLS_EXTENSIONS.includes(ext)

  // ESC 关闭
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // 构建预览 iframe 的 src
  const iframeSrc = useMemo(() => {
    if (isPdf) {
      // PDF 走服务端代理流（鉴权，不暴露 COS URL）
      return streamUrl
    }
    if (isOffice && previewUrl) {
      // Office 文件使用 Microsoft Office Online Viewer（需要公网可访问的 COS 预签名 URL）
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`
    }
    return ''
  }, [streamUrl, previewUrl, isPdf, isOffice])

  if (!open) return null

  const Icon = getFileIcon(fileName)

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* 遮罩 */}
      <div
        className='absolute inset-0 bg-black/60 backdrop-blur-sm'
        onClick={onClose}
      />
      {/* 弹窗 */}
      <div className='relative z-10 mx-4 flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border bg-card shadow-2xl'>
        {/* 标题栏 */}
        <div className='flex shrink-0 items-center gap-3 border-b px-5 py-3'>
          <Icon className='h-5 w-5 text-primary' />
          <span className='min-w-0 flex-1 truncate text-sm font-semibold'>{fileName}</span>
          <a
            href={isPdf ? streamUrl : previewUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
            title='在新窗口打开'
          >
            <ExternalLink className='h-3.5 w-3.5' />
          </a>
          <button
            onClick={onClose}
            className='rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          >
            <X className='h-4 w-4' />
          </button>
        </div>

        {/* 预览区 */}
        <div className='relative flex-1 bg-muted/20'>
          {loading && (
            <div className='absolute inset-0 z-10 flex items-center justify-center bg-card/60'>
              <div className='flex flex-col items-center gap-3'>
                <Loader2 className='h-8 w-8 animate-spin text-primary' />
                <span className='text-sm text-muted-foreground'>加载预览中...</span>
              </div>
            </div>
          )}
          {iframeSrc ? (
            <iframe
              src={iframeSrc}
              className='h-full w-full border-0'
              title={`预览: ${fileName}`}
              sandbox='allow-scripts allow-same-origin allow-popups allow-forms'
            />
          ) : (
            <div className='flex h-full items-center justify-center'>
              <span className='text-sm text-muted-foreground'>无法预览该文件类型</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
